import config from '@/config';
import useAuthStore from '@/stores/authStore';

/**
 * Shared transport for the server-sent-event endpoints.
 *
 * These can't go through the configured Axios client: browser Axios is
 * XHR-backed and buffers the whole response, so there is no way to read a
 * body incrementally (`responseType: 'stream'` is Node-only). `fetch` is the
 * only option, which means CSRF, credentials and error extraction have to be
 * applied by hand — so they're applied here, once, rather than per endpoint.
 *
 * `done` and `error` frames are handled here because every consumer treats
 * them identically; `onEvent` sees only the domain events.
 *
 * @param {object}      params
 * @param {string}      params.path      - Server path, e.g. '/v1/.../agent/'
 * @param {object}      params.body      - JSON request body
 * @param {AbortSignal} [params.signal]  - Cancels the request and the read loop
 * @param {Function}    [params.onEvent] - Called with each parsed domain event
 * @param {Function}    [params.onDone]  - Called once when the stream ends
 * @param {Function}    [params.onError] - Called with an error message string
 * @param {string}      [params.logLabel] - Prefix for unparseable-line warnings
 */
export const openEventStream = async ({
  path,
  body,
  signal,
  onEvent,
  onDone,
  onError,
  logLabel = 'SSE',
}) => {
  const csrfToken = useAuthStore.getState().csrfToken;

  let response;
  try {
    response = await fetch(`${config.serverUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken || '',
      },
      credentials: 'include',
      signal: signal ?? null,
      body: JSON.stringify(body),
    });
  } catch (networkErr) {
    if (networkErr.name === 'AbortError') return;
    onError?.(`Network error: ${networkErr.message}`);
    return;
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const errorBody = await response.json();
      detail = errorBody?.error || errorBody?.detail || detail;
    } catch {
      // ignore parse failure
    }
    onError?.(detail);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let aborted = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE messages are separated by blank lines (\n\n).
      const parts = buffer.split('\n\n');
      buffer = parts.pop(); // last entry may be an incomplete message

      for (const part of parts) {
        if (!part.trim()) continue;
        const dataLine = part.split('\n').find(l => l.startsWith('data: '));
        if (!dataLine) continue;
        let event;
        try {
          event = JSON.parse(dataLine.slice(6));
        } catch {
          console.warn(`[${logLabel}] Unparseable line:`, dataLine);
          continue;
        }

        if (event.type === 'done') {
          onDone?.();
          return;
        }
        if (event.type === 'error') {
          onError?.(event.content ?? 'Unknown error');
          return;
        }
        onEvent?.(event);
      }
    }
  } catch (err) {
    // AbortError is user-initiated cancellation — exit silently.
    if (err.name !== 'AbortError') throw err;
    aborted = true;
  } finally {
    reader.releaseLock();
  }

  // Stream ended without an explicit done event (not applicable if aborted).
  if (!aborted) onDone?.();
};
